import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { SupportingAccountingFirm, RequiredFirmKeys, AddAccountingFirm } from '../../Redux/Actions/strategyService';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
};

function AddAccountingFirmModal({ open, handleClose }) {
  const dispatch = useDispatch();
  const [selectedFirm, setSelectedFirm] = useState('');
  const [supportedFirms, setSupportedFirms] = useState([]);
  const [requiredKeys, setRequiredKeys] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [keysLoading, setKeysLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const entityId = localStorage.getItem("entId");
  // Fetch supported accounting firms on component mount
  useEffect(() => {
    if (open) {
      setLoading(true);
      dispatch(SupportingAccountingFirm())
        .unwrap()
        .then((response) => {
          if (response.statusCode === 200) {
            setSupportedFirms(response.data);
          }
        })
        .catch((error) => {
          console.error('Error fetching supported firms:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, open]);

  // Fetch required keys when a firm is selected
  useEffect(() => {
    if (selectedFirm) {
      setKeysLoading(true);
      setFormData({}); // Reset form data when firm changes
      setRequiredKeys([]); // Reset required keys
      
      dispatch(RequiredFirmKeys(selectedFirm))
        .unwrap()
        .then((response) => {
          if (response.statusCode === 200) {
            setRequiredKeys(response.data);
            
            // Initialize form data with empty values for each required key
            const initialFormData = {};
            response.data.forEach(key => {
              initialFormData[key] = '';
            });
            setFormData(initialFormData);
          }
        })
        .catch((error) => {
          console.error('Error fetching required keys:', error);
        })
        .finally(() => {
          setKeysLoading(false);
        });
    }
  }, [dispatch, selectedFirm]);

  const handleFirmChange = (event) => {
    setSelectedFirm(event.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Transform formData into the required format
    const formattedKeys = Object.entries(formData).map(([name, value]) => ({
      name,
      value
    }));
    
    const submitData = {
      accountingFirm: selectedFirm,
      // entityInfoId: entityId,
      keys: formattedKeys
    };
    
    dispatch(AddAccountingFirm(submitData))
      .unwrap()
      .then((response) => {
        if (response.statusCode === 200) {
          setSnackbar({ open: true, message: 'Accounting firm added successfully!', severity: 'success' });
          handleClose();
        } else if (response.statusCode === 409) {
          setSnackbar({ open: true, message: response.message || 'Firm already exists.', severity: 'error' });
          // Do NOT show failed error modal for 409
        }
      })
      .catch((error) => {
        setSnackbar({ open: true, message: error?.message || 'Error adding accounting firm.', severity: 'error' });
        console.error('Error adding accounting firm:', error);
        // Do NOT show failed error modal
      });
  };

  const handleCancel = () => {
    setSelectedFirm('');
    setFormData({});
    setRequiredKeys([]);
    handleClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCancel}
        aria-labelledby="add-accounting-firm-modal"
        aria-describedby="modal-to-add-new-accounting-firm"
      >
        <Box sx={style}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography id="modal-title" variant="h6" component="h2">
              Add Accounting Firm
            </Typography>
            <IconButton onClick={handleCancel} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please select firm and enter keys.
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="select-firm-label">Select Firm</InputLabel>
                  <Select
                    labelId="select-firm-label"
                    id="select-firm"
                    value={selectedFirm}
                    label="Select Firm"
                    onChange={handleFirmChange}
                    disabled={loading}
                  >
                    {loading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      supportedFirms.map((firm) => (
                        <MenuItem key={firm} value={firm}>
                          {firm}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              
              {keysLoading && (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={24} />
                </Grid>
              )}
              
              {!keysLoading && requiredKeys.length > 0 && (
                <>
                  {requiredKeys.map((key) => (
                    <Grid item xs={12} key={key}>
                      <TextField
                        fullWidth
                        label={key.replace(/_/g, ' ')}
                        name={key}
                        value={formData[key] || ''}
                        onChange={handleInputChange}
                        required
                        margin="normal"
                      />
                    </Grid>
                  ))}
                </>
              )}
              
              <Grid item xs={12} mt={2}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    type="submit" 
                    color="primary"
                    disabled={!selectedFirm || requiredKeys.length === 0 || keysLoading}
                  >
                    Add
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message === 'Duplicate entry: A record with this accounting_firm, entity_info_id already exists'
            ? 'This accounting firm is already linked to your entity.'
            : snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AddAccountingFirmModal; 